import { Controller } from 'stimulus'

// The survey page (surveys/page/_page): follow-up questions show and hide as
// answers change (the same rule as Surveys::Visibility), picking an answer
// moves on to the next unanswered question, and answers save as the person
// goes. Choices save at once; text saves when the box loses focus, and any
// unsaved text is flushed when the page is hidden. Send checks every shown
// required question first. In previews (data-survey-form-preview="true")
// nothing is saved and Send shows the thank-you message in place.
export default class extends Controller {
  static targets = ['page', 'form', 'question', 'number', 'progress', 'bar', 'saved', 'left', 'thanks'];

  connect() {
    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.dirty = false
    this.saving = null
    this.flush = this.flush.bind(this)
    window.addEventListener('pagehide', this.flush)
    this.refresh({ animate: false })
  }

  disconnect() {
    window.removeEventListener('pagehide', this.flush)
    clearTimeout(this.timer)
  }

  get preview() {
    return this.data.get('preview') === 'true'
  }

  get autosaveUrl() {
    return this.data.get('autosave')
  }

  pick(event) {
    this.refresh({ animate: true })
    this.queueSave(0)
    const input = event.target
    if (input.type === 'radio') this.advanceFrom(input.closest('[data-target~="survey-form.question"]'))
  }

  typing() {
    this.dirty = true
  }

  typed() {
    this.refresh({ animate: true })
    this.queueSave(0)
  }

  send(event) {
    const missing = this.visibleQuestions().filter(q => q.dataset.required === 'true' && !this.answered(q))
    this.questionTargets.forEach(q => q.classList.toggle('is-missing', missing.includes(q)))
    if (missing.length) {
      event.preventDefault()
      this.scrollTo(missing[0])
      this.refresh({ animate: false })
      return
    }
    if (this.preview) {
      event.preventDefault()
      this.pageTarget.hidden = true
      if (this.hasThanksTarget) this.thanksTarget.hidden = false
      this.scrollTo(this.element)
      return
    }
    // Answers to follow-ups that ended up hidden aren't sent (the server
    // clears them too).
    const hiddenInputs = this.questionTargets.filter(q => q.hidden).flatMap(q => Array.from(q.querySelectorAll('input, textarea')))
    for (let i = 0; i < hiddenInputs.length; i += 1) hiddenInputs[i].disabled = true
    this.sending = true
  }

  again() {
    this.formTarget.reset()
    this.questionTargets.forEach(q => q.classList.remove('is-missing'))
    this.pageTarget.hidden = false
    if (this.hasThanksTarget) this.thanksTarget.hidden = true
    this.refresh({ animate: false })
    this.scrollTo(this.element)
  }

  // --- state -------------------------------------------------------------

  refresh({ animate }) {
    const shown = new Set()
    this.questionTargets.forEach((q) => {
      const source = q.dataset.showIfQuestion
      let visible = true
      if (source) {
        const sourceEl = this.questionTargets.find(el => el.dataset.questionId === source)
        const wanted = (q.dataset.showIfChoices || '').split(',').filter(Boolean)
        visible = !!sourceEl && shown.has(source) && this.chosen(sourceEl).some(id => wanted.includes(id))
      }
      if (visible) shown.add(q.dataset.questionId)
      if (q.hidden && visible && animate) {
        q.classList.add('reveal')
        setTimeout(() => q.classList.remove('reveal'), 400)
      }
      q.toggleAttribute('hidden', !visible)
      q.classList.toggle('done', this.answered(q))
      this.lightStars(q)
    })

    const visible = this.visibleQuestions()
    visible.forEach((q, i) => {
      const number = q.querySelector('[data-target~="survey-form.number"]')
      if (number) number.textContent = `${i + 1}.`
    })
    const done = visible.filter(q => this.answered(q)).length
    const left = visible.filter(q => q.dataset.required === 'true' && !this.answered(q)).length
    if (this.hasProgressTarget) this.progressTarget.textContent = `${done} of ${visible.length}`
    if (this.hasBarTarget) this.barTarget.style.width = `${visible.length ? Math.round((done / visible.length) * 100) : 0}%`
    if (this.hasLeftTarget) {
      if (left === 0) this.leftTarget.textContent = 'Ready to send'
      else this.leftTarget.textContent = left === 1 ? '1 more question to answer' : `${left} more questions to answer`
    }
    visible.forEach((q) => { if (this.answered(q)) q.classList.remove('is-missing') })
  }

  visibleQuestions() {
    return this.questionTargets.filter(q => !q.hidden)
  }

  chosen(question) {
    return Array.from(question.querySelectorAll('input[type=radio]:checked, input[type=checkbox]:checked'))
      .map(input => input.value)
      .filter(Boolean)
  }

  answered(question) {
    if (this.chosen(question).length) return true
    const box = question.querySelector('input[type=text], textarea')
    return !!(box && box.value.trim())
  }

  lightStars(question) {
    const stars = Array.from(question.querySelectorAll('.rp-star'))
    if (!stars.length) return
    const picked = stars.findIndex(star => star.querySelector('input').checked)
    stars.forEach((star, i) => star.classList.toggle('lit', picked >= 0 && i <= picked))
  }

  advanceFrom(question) {
    const visible = this.visibleQuestions()
    const index = visible.indexOf(question)
    const next = visible.slice(index + 1).find(q => !this.answered(q))
    const target = next || this.formTarget.querySelector('.rp-send')
    if (target) setTimeout(() => this.scrollTo(target), 140)
  }

  // Scrolls the preview's phone frame when there is one, else the page.
  scrollTo(element) {
    const behavior = this.reduced ? 'auto' : 'smooth'
    const frame = this.element.closest('[data-survey-scroller]')
    if (frame) {
      const top = element.getBoundingClientRect().top - frame.getBoundingClientRect().top + frame.scrollTop - 56
      frame.scrollTo({ top: Math.max(0, top), behavior })
    } else {
      const top = element.getBoundingClientRect().top + window.pageYOffset - 56
      window.scrollTo({ top: Math.max(0, top), behavior })
    }
  }

  // --- saving ------------------------------------------------------------

  queueSave(delay) {
    if (this.preview || !this.autosaveUrl) return
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.save(), delay)
  }

  body() {
    const data = new FormData(this.formTarget)
    data.delete('send')
    data.delete('_method')
    return data
  }

  save() {
    if (this.saving) {
      this.saveAgain = true
      return
    }
    this.dirty = false
    this.setSaved('Saving…')
    const token = document.querySelector('meta[name="csrf-token"]')
    this.saving = fetch(this.autosaveUrl, {
      method: 'PATCH',
      body: this.body(),
      credentials: 'same-origin',
      redirect: 'manual',
      headers: { Accept: 'application/json', 'X-CSRF-Token': token ? token.content : '' }
    }).then((response) => {
      if (response.status === 409) {
        window.location.reload()
        return
      }
      this.setSaved(response.ok ? 'Saved' : 'Not saved', !response.ok)
    }).catch(() => {
      this.setSaved('Not saved', true)
    }).finally(() => {
      this.saving = null
      if (this.saveAgain) {
        this.saveAgain = false
        this.save()
      }
    })
  }

  flush() {
    if (!this.dirty || this.sending || this.preview || !this.autosaveUrl) return
    const token = document.querySelector('meta[name="csrf-token"]')
    fetch(this.autosaveUrl, {
      method: 'PATCH',
      body: this.body(),
      credentials: 'same-origin',
      keepalive: true,
      headers: { Accept: 'application/json', 'X-CSRF-Token': token ? token.content : '' }
    })
    this.dirty = false
  }

  setSaved(text, error = false) {
    if (!this.hasSavedTarget) return
    this.savedTarget.textContent = text
    this.savedTarget.classList.toggle('is-error', error)
  }
}
